import { COLORS, Text } from "@asayinc/component-library";
import { Box, Stack } from "@mui/material";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";

import { Card } from "src/components/atoms/Card";
import {
  UpcomingDeadline,
  useUpcomingDeadlinesQuery,
} from "src/store/dashboard";

function DeadlineItem({ deadline }: { deadline: UpcomingDeadline }) {
  const navigate = useNavigate();
  const color = "#1976D2";
  const bgColor = "#E3F2FD";

  // Calculate time difference
  const now = dayjs();
  const deadlineDate = dayjs(deadline.deadlineAt);
  const hoursDiff = deadlineDate.diff(now, "hour");
  const daysDiff = deadlineDate.diff(now, "day");

  // Show hours if less than 24 hours, otherwise show days
  const isHours = hoursDiff < 24;
  const timeValue = isHours ? hoursDiff : daysDiff;

  const getEntityRoute = (entityType: string, entityId: string) => {
    switch (entityType) {
      case "proxy_event":
        return `/proxy-events/${entityId}/details`;
      case "share_range_analysis_event":
        return `/share-range-analysis-events/${entityId}/reports`;
      case "location_analysis_event":
        return `/location-analysis-events/${entityId}/reports`;
      case "nobo_event":
        return `/nobo-events/${entityId}/details`;
      case "class_action_event":
        return `/class-action-events/${entityId}/details`;
      default:
        return null;
    }
  };

  const handleClick = () => {
    const route = getEntityRoute(deadline.entityType, deadline.entityId);
    if (route) {
      navigate(route);
    }
  };

  return (
    <Box
      onClick={handleClick}
      sx={{
        py: 3,
        px: 2,
        borderBottom: `1px solid ${COLORS.P_OUTLINE}`,
        "&:last-child": { borderBottom: "none" },
        cursor: "pointer",
        transition: "background-color 0.2s ease",
        "&:hover": {
          backgroundColor: `${color}08`,
        },
      }}
    >
      <Stack direction="row" alignItems="center" spacing={2}>
        <Box
          sx={{
            minWidth: 60,
            textAlign: "center",
            py: 1,
            px: 2,
            borderRadius: 1,
            backgroundColor: bgColor,
          }}
        >
          <Text
            variant="h5"
            sx={{ color: color, fontWeight: 700, lineHeight: 1 }}
          >
            {timeValue}
          </Text>
          <Text variant="caption" sx={{ color: color }}>
            {isHours
              ? timeValue === 1
                ? "hour"
                : "hours"
              : timeValue === 1
              ? "day"
              : "days"}
          </Text>
        </Box>
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Text variant="subtitle2" sx={{ mb: 0.5 }}>
            {deadline.title}
          </Text>
          <Text variant="body3" sx={{ color: COLORS.P_MED_LOW_EMPHASIS }}>
            {deadline.issuerName}
            {deadline.jobNumber ? ` • ${deadline.jobNumber}` : ""}
          </Text>
          <Text variant="caption" sx={{ color: COLORS.P_MED_LOW_EMPHASIS }}>
            {dayjs(deadline.deadlineAt).format("MMM D, YYYY [at] h:mm A")}
          </Text>
        </Box>
      </Stack>
    </Box>
  );
}

export function UpcomingDeadlinesSection() {
  const { data: upcomingDeadlines } = useUpcomingDeadlinesQuery();

  return (
    <Card title="Upcoming Deadlines">
      <Box
        sx={{
          maxHeight: 400,
          overflowY: "auto",
          border: `1px solid ${COLORS.P_OUTLINE}`,
          borderRadius: 1,
        }}
      >
        {upcomingDeadlines && upcomingDeadlines.length > 0 ? (
          upcomingDeadlines.map((deadline) => (
            <DeadlineItem key={deadline.entityId} deadline={deadline} />
          ))
        ) : (
          <Box sx={{ p: 4, textAlign: "center" }}>
            <Text variant="body3" sx={{ color: COLORS.P_MED_LOW_EMPHASIS }}>
              No upcoming deadlines
            </Text>
          </Box>
        )}
      </Box>
    </Card>
  );
}
